import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import { useFlags } from '@atlaskit/flag';
import Form, { Field, FormFooter } from '@atlaskit/form';
import { ModalFooter } from '@atlaskit/modal-dialog';
import { Box, Flex } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import TextField from '@atlaskit/textfield';
import { useEffect, useState } from 'react';

import { useApiRequest } from '@/components/common/hooks/useApiRequest';


interface FormData {
  hostname: string;
  oauthClientId: string;
  oauthClientSecret: string;
}

type DisplayMode = 'view' | 'edit';

export const DatabricksConfigForm = ({
  closeModal,
  cloudSiteId,
}: {
  closeModal: () => void;
  cloudSiteId: string;
}) => {
  const { data, loading, execute } = useApiRequest();
  const { showFlag } = useFlags();
  const [displayMode, setDisplayMode] = useState<DisplayMode>('view');
  useEffect(() => {
    const fetchSavedHost = async () => {
      await execute({
        url: `/gateway/api/aura-utils-v1/ui/client/${cloudSiteId}`,
        method: 'GET',
      });
    };

    fetchSavedHost();
  }, [cloudSiteId]);

  useEffect(() => {
    if (data && data.properties) {
      setFormData({
        hostname: data.properties.host,
        oauthClientId: data.properties.oauthCid,
        oauthClientSecret: data.properties.oauthSecret,
      });
      setDisplayMode('view'); // Show view mode when data is loaded
    } else {
      setDisplayMode('edit'); // Show edit mode when no existing data
    }
  }, [data]);
  const [formData, setFormData] = useState<FormData>({
    hostname: '',
    oauthClientId: '',
    oauthClientSecret: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleFormSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      // Trim all form values before processing
      const trimmedData = {
        host: data.hostname.trim().replace(/\/+$/, ''),
        oauthCid: data.oauthClientId.trim(),
        oauthSecret: data.oauthClientSecret.trim(),
        clientId: cloudSiteId,
        tenantId: cloudSiteId,
      };
      const value = JSON.stringify(trimmedData);

      // Simulate network call
      // await new Promise(resolve => setTimeout(resolve, 2000));

      // Placeholder for actual API call
      const response = await fetch('/gateway/api/aura-utils-v1/ui/client', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key: `${cloudSiteId}`, value }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save configuration: ${response.status}`);
      }


      showFlag({
        appearance: 'success',
        title: `Databricks configuration successfully saved.`,
        isAutoDismiss: true,
      });
      console.log(
        'Form submitted successfully with response:',
        response.status
      );
      setDisplayMode('view'); // Switch back to view mode after successful save
      // Handle success (e.g., show success message, redirect, etc.)
    } catch (error) {
      showFlag({
        appearance: 'error',
        title: `Databricks configuration failed to save. Please try again or contact support.`,
        isAutoDismiss: true,
      });
      console.error('Error submitting form:', error);
      // Handle error (e.g., show error message)
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = () => {
    setDisplayMode('edit');
    // Clear the OAuth client secret when entering edit mode
    setFormData(prev => ({
      ...prev,
      oauthClientSecret: '',
    }));
  };

  const handleCancelClick = () => {
    if (displayMode === 'edit') {
      setDisplayMode('view');
      // Reset form data to original values when canceling edit
      if (data && data.properties) {
        setFormData({
          hostname: data.properties.host,
          oauthClientId: data.properties.oauthCid,
          oauthClientSecret: data.properties.oauthSecret,
        });
      }
    } else {
      closeModal();
    }
  };
  if (loading) {
    return (
      <Flex direction='row' justifyContent='center' alignItems='center'>
        <Box padding='space.300'>
          <Spinner size='large' />
        </Box>
      </Flex>
    );
  }

  const ViewField = ({ label, value }: { label: string; value: string }) => (
    <Box paddingBlock='space.200'>
      <div style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
        {label}
      </div>
      <Box
        backgroundColor='color.background.neutral.subtle'
        padding='space.100'
        style={{
          border: '1px solid var(--ds-border)',
          borderRadius: '3px',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {value || 'Not configured'}
      </Box>
    </Box>
  );

  if (displayMode === 'view') {
    return (
      <Flex direction='column' alignItems='center'>
        <Box style={{ width: '100%' }}>
          <ViewField label="Databricks Host" value={formData.hostname} />
          <ViewField label="OAuth Client ID" value={formData.oauthClientId} />

          <ModalFooter>
            <ButtonGroup label='Form view options'>
              <Button
                appearance='subtle'
                onClick={handleCancelClick}
                type='button'
              >
                Cancel
              </Button>
              <Button
                appearance='primary'
                onClick={handleEditClick}
                type='button'
              >
                Edit
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction='column' alignItems='center'>
      <Box style={{ width: '100%' }}>
        <Box paddingBlock='space.200'>
          <SectionMessage appearance="information">
            <h4 style={{ marginTop: '0', marginBottom: '12px' }}>Before you begin</h4>
            <ul style={{ marginLeft: '16px', paddingLeft: '0', fontSize: '14px' }}>
              <li style={{ marginBottom: '12px' }}>
                By connecting this source, you may be authorizing Atlassian to access or ingest sensitive data. See&nbsp;
                <a href="https://support.atlassian.com/organization-administration/docs/manage-rovo-connectors/" target="_blank" rel="noopener noreferrer">Rovo data, privacy and usage guidelines</a>&nbsp;
                for more information.
              </li>
              <li style={{ marginBottom: '12px' }}>
                You need Databricks global admin permissions to connect Databricks to Rovo.
              </li>
              <li style={{ marginBottom: '12px' }}>
                We encourage you to review your permissions in&nbsp;Databricks. Content that has not been restricted will appear for all users in search results.&nbsp;
                <a href="https://support.atlassian.com/organization-administration/docs/manage-rovo-connectors/" target="_blank" rel="noopener noreferrer">More on connectors and permissions</a>
              </li>
              <li style={{ marginBottom: '12px' }}>
                We strongly recommend following our&nbsp;
                <a href="/databricks/help#how-to-configure" target="_blank" rel="noopener noreferrer">support documentation for step-by-step instructions</a>&nbsp;
                due to the number of specific steps to connect to Databricks.
              </li>
              <li style={{ marginBottom: '12px' }}>
                <a href="/databricks/help#what-gets-indexed" target="_blank" rel="noopener noreferrer">See our support documentation</a>&nbsp;for information on what content types the&nbsp;Databricks&nbsp;Rovo connector supports.
              </li>
              <li style={{ marginBottom: '12px' }}>
                Rovo will always respect permissions. Users will only ever see events that they already have access to.
              </li>
            </ul>
          </SectionMessage>
        </Box>

        <Form onSubmit={handleFormSubmit}>
          {({ formProps, submitting }) => (
            <form {...formProps}>
              <Field
                name='hostname'
                defaultValue={formData.hostname}
                label='Databricks Host'
                isRequired
              >
                {({ fieldProps }) => (
                  <TextField
                    {...fieldProps}
                    placeholder='https://xxx-xxx-xxx.cloud.databricks.com'
                    value={formData.hostname}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        hostname: (e.target as HTMLInputElement).value,
                      }))
                    }
                  />
                )}
              </Field>
              <Field
                name='oauthClientId'
                defaultValue={formData.oauthClientId}
                label='OAuth Client ID'
                isRequired
              >
                {({ fieldProps }) => (
                  <TextField
                    {...fieldProps}
                    placeholder='xxxx-xxxx-xxxx-xxxx'
                    value={formData.oauthClientId}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        oauthClientId: (e.target as HTMLInputElement).value,
                      }))
                    }
                  />
                )}
              </Field>
              <Field
                name='oauthClientSecret'
                defaultValue={formData.oauthClientSecret}
                label='OAuth Client Secret'
                isRequired
              >
                {({ fieldProps }) => (
                  <TextField
                    {...fieldProps}
                    type='password'
                    placeholder='Enter OAuth Client Secret'
                    value={formData.oauthClientSecret}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        oauthClientSecret: (e.target as HTMLInputElement).value,
                      }))
                    }
                  />
                )}
              </Field>

              <Box paddingBlock='space.300'>
                <SectionMessage appearance="information">
                  <p>By clicking &ldquo;Save&rdquo;, you:</p>
                  <ul style={{ marginLeft: '16px', paddingLeft: '0' }}>
                    <li style={{ marginBottom: '8px' }}>
                      agree that Atlassian will collect data from the third-party product and process it across your Atlassian products in accordance with the <a href="https://www.atlassian.com/legal/atlassian-customer-agreement#intro" target="_blank" rel="noopener noreferrer">Atlassian Customer Agreement</a> and <a href="https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      confirm that you have obtained the rights, consents and authorizations necessary for Atlassian to use such data as described in the <a href="https://www.atlassian.com/legal/atlassian-customer-agreement#intro" target="_blank" rel="noopener noreferrer">Atlassian Customer Agreement</a>
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      agree that you have reviewed and are compliant with the third-party&rsquo;s applicable terms and policies governing the use of the third-party product
                    </li>
                  </ul>
                </SectionMessage>
              </Box>

              <FormFooter>
                <ModalFooter>
                  <ButtonGroup label='Form submit options'>
                    <Button
                      appearance='subtle'
                      onClick={handleCancelClick}
                      type='button'
                    >
                      Cancel
                    </Button>
                    <Button
                      type='submit'
                      appearance='primary'
                      isLoading={submitting || isSubmitting}
                    >
                      Save
                    </Button>
                  </ButtonGroup>
                </ModalFooter>
              </FormFooter>
            </form>
          )}
        </Form>
      </Box>
    </Flex>
  );
};
